import { useMemo } from 'react'
import { mergeHypothesisAudiences } from '@/utils/audience-builder'
import { mergeWarmAudiences } from '@/utils/warm-audiences'
import { mergeLookalikeAudiences } from '@/utils/lookalike-audiences'
import { mergeAccountAudiences } from '@/utils/account-audiences'

const readHypothesisMap = (getStepContentData) => {
    if (typeof getStepContentData !== 'function') return undefined
    return getStepContentData('salesFunnelSetup')?.hypothesisAudiences
}

const readWarmAudiences = (getStepContentData) => {
    if (typeof getStepContentData !== 'function') return undefined
    return getStepContentData('salesFunnelSetup')?.warmAudiences
}

const readLookalikeMap = (getStepContentData) => {
    if (typeof getStepContentData !== 'function') return undefined
    return getStepContentData('salesFunnelSetup')?.lookalikeAudiences
}

const readAccountAudiences = (getStepContentData) => {
    if (typeof getStepContentData !== 'function') return undefined
    return getStepContentData('salesFunnelSetup')?.accountAudiences
}

const hasWarmAudiences = (warmAudiences) => Array.isArray(warmAudiences) && warmAudiences.length > 0

const hasLookalikes = (lookalikeMap) => !!lookalikeMap && Object.keys(lookalikeMap).length > 0

const hasAccountAudiences = (accountMap) => !!accountMap && Object.keys(accountMap).length > 0

const mergeAll = (audiences, hypothesisMap, warmAudiences, lookalikeMap, accountMap) =>
    mergeAccountAudiences(
        mergeLookalikeAudiences(
            mergeWarmAudiences(mergeHypothesisAudiences(audiences, hypothesisMap), warmAudiences),
            lookalikeMap,
        ),
        accountMap,
    )

/**
 * Adds the wizard's AI audiences, chosen warm audiences, lookalikes and account audiences to a catalogue audiences list. Returns
 * the very same array when the wizard has none of them, so screens behave exactly as before.
 */
export const useAudiencesWithHypotheses = (audiences, getStepContentData) => {
    const hypothesisMap = readHypothesisMap(getStepContentData)
    const warmAudiences = readWarmAudiences(getStepContentData)
    const lookalikeMap = readLookalikeMap(getStepContentData)
    const accountMap = readAccountAudiences(getStepContentData)
    return useMemo(
        () => mergeAll(audiences, hypothesisMap, warmAudiences, lookalikeMap, accountMap),
        [audiences, hypothesisMap, warmAudiences, lookalikeMap, accountMap],
    )
}

/** Same as above for query data shaped as { Audience: { audiences } }. */
export const useAudienceDataWithHypotheses = (audiencesData, getStepContentData) => {
    const hypothesisMap = readHypothesisMap(getStepContentData)
    const warmAudiences = readWarmAudiences(getStepContentData)
    const lookalikeMap = readLookalikeMap(getStepContentData)
    const accountMap = readAccountAudiences(getStepContentData)
    return useMemo(() => {
        const hasHypotheses = !!hypothesisMap && Object.keys(hypothesisMap).length > 0
        if (
            !hasHypotheses &&
            !hasWarmAudiences(warmAudiences) &&
            !hasLookalikes(lookalikeMap) &&
            !hasAccountAudiences(accountMap)
        )
            return audiencesData
        const base = audiencesData?.Audience?.audiences || []
        return {
            ...(audiencesData || {}),
            Audience: {
                ...(audiencesData?.Audience || {}),
                audiences: mergeAll(base, hypothesisMap, warmAudiences, lookalikeMap, accountMap),
            },
        }
    }, [audiencesData, hypothesisMap, warmAudiences, lookalikeMap, accountMap])
}
